import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import {
  Container,
  Box,
  Stack,
  Title,
  Text,
  PasswordInput,
  PinInput,
  Button,
  Alert,
  Image,
  Group,
} from "@mantine/core";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import rNameLogo from "../../assets/Reppi.svg";

function ResetPinPage() {
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get the JWT token from URL parameters
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError("Invalid reset link. Please request a new PIN reset.");
    }
  }, [searchParams]);

  const validateInputs = () => {
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return false;
    }
    if (pin.length !== 4) {
      setError("PIN must be exactly 4 digits.");
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateInputs()) {
      return;
    }

    if (!token) {
      setError("Invalid reset token. Please request a new PIN reset.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(getApiEndpoint("/v1/user/reset-pin"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          token: token,
          password: password,
          pin: parseInt(pin),
        }),
      });

      await response.json();

      if (response.ok) {
        setSuccess(
          "Your PIN has been successfully reset. You can now use your new PIN."
        );
        setPassword("");
        setPin("");
      } else {
        setError(
          "Failed to reset PIN. Please try again or request a new reset link."
        );
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      <Container style={{ width: "100%", maxWidth: "500px" }}>
        <Box
          bg="white"
          style={{
            borderRadius: "8px",
            boxShadow: "var(--shadow-md)",
            padding: "60px 48px",
            textAlign: "center",
          }}
        >
          <Box
            mb={40}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/home")}
          >
            <Image src={rNameLogo} alt="Reppi" h={60} w="auto" mx="auto" />
          </Box>

          <Stack gap="md" mb={32}>
            <Title order={2} size="2xl" fw={600} c="var(--color-text-primary)">
              Reset Your Security PIN
            </Title>
            <Text size="sm" c="var(--color-text-secondary)" lh={1.5}>
              Enter your password and create a new 4-digit PIN below.
            </Text>
          </Stack>

          {/* Success Alert */}
          {success && (
            <Alert
              icon={<IconCheck size="1rem" />}
              title="Success!"
              color="green"
              mb="lg"
              ta="left"
            >
              {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              icon={<IconAlertCircle size="1rem" />}
              title="Error"
              color="red"
              mb="lg"
              ta="left"
            >
              {error}
            </Alert>
          )}

          {success ? (
            <Group justify="center" mb={32}>
              <Button size="md" onClick={handleBackToLogin}>
                Go to Login
              </Button>
            </Group>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              style={{ textAlign: "left" }}
            >
              <Stack gap="md">
                <PasswordInput
                  label="Current Password"
                  placeholder="Enter your current password"
                  value={password}
                  onChange={event => setPassword(event.currentTarget.value)}
                  required
                  size="md"
                  styles={{
                    label: {
                      fontWeight: 500,
                      marginBottom: 8,
                    },
                  }}
                />

                <Box>
                  <Text size="sm" fw={500} mb={8}>
                    New 4-Digit PIN
                  </Text>
                  <Group justify="center">
                    <PinInput
                      length={4}
                      value={pin}
                      onChange={setPin}
                      placeholder="○"
                      type="number"
                      size="lg"
                      mask
                    />
                  </Group>
                  <Text c="dimmed" size="xs" ta="center" mt={8}>
                    Choose a PIN that you'll remember but others won't guess
                  </Text>
                </Box>

                <Button
                  type="submit"
                  size="md"
                  loading={loading}
                  disabled={!token}
                  fullWidth
                >
                  {loading ? "Resetting..." : "Reset PIN"}
                </Button>
              </Stack>
            </Box>
          )}

          <Group justify="center" mt={24}>
            <Button variant="subtle" size="sm" onClick={handleBackToLogin}>
              Back to Login
            </Button>
          </Group>
        </Box>
      </Container>
    </Box>
  );
}

export default ResetPinPage;
